import { motion } from 'framer-motion';
import type { Category, Transaction } from '../../data/types';
import { cn } from '../../lib/cn';
import { formatCurrency } from '../../lib/currency';
import { formatRelativeTime } from '../../lib/date';
import { springQuick, tapScale } from '../../tokens';

type Props = {
  transaction: Transaction;
  category: Category | undefined;
  currency: string;
  now: Date;
  onClick?: () => void;
  /**
   * Override the second-line label. Defaults to a "Nm ago" relative
   * timestamp; the Activity tab passes the category name instead so
   * the date-group header isn't echoed twice.
   */
  subtitle?: string;
};

/**
 * Minimal Recent transactions row for the Today tab — small color dot,
 * description, time-ago line, amount right-aligned. The Activity tab
 * uses the full ListRow icon-square treatment instead.
 */
export function RecentRow({
  transaction,
  category,
  currency,
  now,
  onClick,
  subtitle,
}: Props) {
  const isExpense = transaction.type === 'expense';
  const amount = formatCurrency(transaction.amount, { currency });
  const description =
    transaction.description?.trim() || category?.name || 'Transaction';
  const dotColor = category?.color ?? '#AEAEB2';

  const isSpread =
    typeof transaction.spreadDays === 'number' && transaction.spreadDays > 1;

  const content = (
    <>
      <span
        className="w-2.5 h-2.5 rounded-pill shrink-0 mt-[7px]"
        style={{ backgroundColor: dotColor }}
        aria-hidden
      />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-[15px] font-medium text-text-primary truncate">
            {description}
          </span>
          {isSpread ? (
            <span
              aria-label={`Spread over ${transaction.spreadDays} days`}
              className="inline-flex items-center justify-center rounded-pill px-2 py-[1px] bg-accent/10 text-accent text-[10px] font-semibold uppercase tracking-[0.04em] leading-none shrink-0"
            >
              ÷ {transaction.spreadDays}d
            </span>
          ) : null}
        </div>
        <div className="text-[12px] text-text-secondary mt-0.5">
          {subtitle ?? formatRelativeTime(transaction.createdAt, now)}
        </div>
      </div>
      <div
        className={cn(
          'text-[15px] font-mono tabular-nums shrink-0 text-right',
          isExpense ? 'text-negative' : 'text-positive',
        )}
      >
        <div>
          {isExpense ? '-' : '+'}
          {amount}
        </div>
        {isSpread && transaction.spreadDays ? (
          <div className="text-[10px] font-mono text-text-tertiary tabular-nums mt-0.5">
            {(transaction.amount / transaction.spreadDays).toFixed(2)}/day
          </div>
        ) : null}
      </div>
    </>
  );

  const baseClasses =
    'w-full flex items-start gap-3 py-3 px-card-pad text-left min-h-tap';

  if (onClick) {
    return (
      <motion.button
        type="button"
        onClick={onClick}
        whileTap={tapScale}
        transition={springQuick}
        className={baseClasses}
      >
        {content}
      </motion.button>
    );
  }
  return <div className={baseClasses}>{content}</div>;
}
